import type { IHelpdeskErrorResponse } from "@kuika/kuika-cl-model";

export interface IHelpdeskErrorInfo {
  status: number;
  code: string | null;
  message: string;
}

interface IAxiosLikeHeaders {
  "retry-after"?: string | number | null;
}

interface IAxiosLikeResponse {
  status?: number;
  data?: IHelpdeskErrorResponse | string | null;
  headers?: IAxiosLikeHeaders | null;
}

interface IAxiosLikeError {
  response?: IAxiosLikeResponse;
  message?: string;
}

const GENERIC_MESSAGE: string = "The helpdesk could not be reached. Please try again.";
const TOO_MANY_REQUESTS: number = 429;

/** The rate-limit message; `Retry-After` (seconds) when CoreApi passed it on. */
export function busyMessage(retryAfterSeconds: number | null): string {
  if (retryAfterSeconds && retryAfterSeconds > 0) {
    const unit: string = retryAfterSeconds === 1 ? "second" : "seconds";
    return `The helpdesk is busy. Please try again in ${retryAfterSeconds} ${unit}.`;
  }
  return "The helpdesk is busy. Please try again in a moment.";
}

function readRetryAfter(headers: IAxiosLikeHeaders | null | undefined): number | null {
  const raw: string | number | null | undefined = headers?.["retry-after"];
  const seconds: number = Number(raw);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : null;
}

/** Reads CoreApi's helpdesk error body (`{ message, code }`) without leaking raw error details. */
export function readHelpdeskError(error: unknown): IHelpdeskErrorInfo {
  const response: IAxiosLikeResponse | undefined = (error as IAxiosLikeError)?.response;
  const status: number = response?.status ?? 0;
  const data: IHelpdeskErrorResponse | string | null | undefined = response?.data;
  const body: IHelpdeskErrorResponse | null = data && typeof data === "object" ? data : null;
  if (status === TOO_MANY_REQUESTS) {
    return { status, code: body?.code ?? null, message: busyMessage(readRetryAfter(response?.headers)) };
  }
  return {
    status,
    code: body?.code ?? null,
    message: body?.message || GENERIC_MESSAGE
  };
}

/** The organization has no running SLA contract: tickets and messages are refused (contract §8). */
export function isAccountRestricted(error: unknown): boolean {
  return readHelpdeskError(error).code === "ACCOUNT_RESTRICTED";
}
